/**
 * localStorage key under which the user's last route selection is persisted.
 *
 * Marked `as const` so consumers see the literal string type rather than
 * a widened `string` — useful if the key is ever needed as a discriminator
 * or in a literal-typed key map.
 */
import { reportHealthError, HEALTH_ID } from '../analytics/appHealthEvents'

export const KEY = 'metro:routeSelection' as const

/**
 * Maximum number of entries kept in {@link PersistedRouteSelection.recentStationSlugs}.
 *
 * The recent list is forward-looking — it backs a future
 * search-suggestions UI ("recent stations" chips). Capping at 10 keeps
 * the localStorage footprint bounded and matches the rough size of an
 * on-screen suggestions list.
 */
export const RECENT_STATIONS_CAP = 10

/**
 * Shape of a persisted route selection.
 *
 * Two complementary fields:
 * - `lastFromStationSlug` — the slug of the most recently chosen
 *   origin (`from`) station, or `null` if cleared. Used to restore
 *   the user's "anchor" on cold start so a returning user doesn't have
 *   to re-pick their usual starting point.
 * - `recentStationSlugs` — most-recently-touched first, deduplicated by
 *   slug, capped to {@link RECENT_STATIONS_CAP}. Includes both From
 *   and To stations the user has interacted with. Forward-looking:
 *   nothing reads it yet; it's written so a future search-suggestions
 *   feature can light up immediately for returning users.
 *
 * Note: there is no persisted `to` slug. On cold start the To slot
 * stays empty so the user can build a new route on revisit — only the
 * URL deeplink path (`?from=&to=`) restores the destination.
 */
export interface PersistedRouteSelection {
  readonly lastFromStationSlug: string | null
  readonly recentStationSlugs: readonly string[]
}

function isStringOrNull(value: unknown): value is string | null {
  return value === null || typeof value === 'string'
}

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
}

/**
 * Runtime type-guard for {@link PersistedRouteSelection}.
 *
 * Validates that `value` is a non-null object exposing both
 * `lastFromStationSlug` (string or null) and `recentStationSlugs`
 * (array of strings). Anything else — including the legacy
 * `{ fromStationSlug, toStationSlug }` shape from earlier dev
 * builds — is rejected, which causes {@link load} to behave as if no
 * prior selection exists. The feature is behind a flag with no
 * production users, so a silent reset is safer than a half-hearted
 * migration.
 */
function isPersistedShape(value: unknown): value is PersistedRouteSelection {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return isStringOrNull(v.lastFromStationSlug) && isStringArray(v.recentStationSlugs)
}

/**
 * Read the persisted route selection from localStorage.
 *
 * Returns `null` if:
 * - nothing has been stored yet,
 * - the stored value is not valid JSON,
 * - the stored payload does not match {@link PersistedRouteSelection}
 *   (this includes the legacy `{ fromStationSlug, toStationSlug }`
 *   shape — no migration, equivalent to a fresh start),
 * - localStorage access throws (private mode, disabled storage, etc.).
 *
 * Never throws — callers can treat a `null` return as "no prior selection".
 */
export function load(): PersistedRouteSelection | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw === null) return null
    const parsed = JSON.parse(raw) as unknown
    return isPersistedShape(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * Persist a route selection to localStorage, overwriting any previous value.
 *
 * Silently swallows errors from `JSON.stringify` (cyclic input — not
 * expected for this shape) and from `localStorage.setItem` (quota exceeded,
 * private-mode browsers, disabled storage). Persistence is best-effort:
 * the app must remain functional without it.
 */
export function save(value: PersistedRouteSelection): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(value))
  } catch (err: unknown) {
    // localStorage may be unavailable (private mode, quota). The app stays
    // functional without it (best-effort), but §3 `persistence_write_failed`
    // surfaces the degraded persistence as a `warning` (fallback=default — the
    // selection just won't survive reload). Health emit must not re-throw.
    reportHealthError(
      HEALTH_ID.persistenceWriteFailed,
      `routeSelection save failed: ${String(err)}`,
      {
        domain: 'persistence',
        severity: 'warning',
        fallback: 'default',
      },
    )
  }
}

/**
 * Remove the persisted route selection from localStorage.
 *
 * No-op if nothing is stored. Errors from localStorage access are
 * swallowed for the same reasons as {@link save}.
 */
export function clear(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // localStorage may be unavailable (private mode, quota). Drop silently.
  }
}

/**
 * Prepend `slug` to `list`, deduplicating by slug (any prior occurrence
 * is removed so the slug ends up at the head exactly once), and cap the
 * result to `cap` entries.
 *
 * Pure: returns a new array; never mutates `list`. Returned length is
 * always at most `cap`, even when `list.length > cap` on input
 * (defensive slice).
 *
 * @param list — the existing recent list (most-recent first)
 * @param slug — the slug to insert at the head
 * @param cap — maximum size of the returned list (default {@link RECENT_STATIONS_CAP})
 */
export function prependRecent(
  list: readonly string[],
  slug: string,
  cap: number = RECENT_STATIONS_CAP,
): string[] {
  const filtered = list.filter((entry) => entry !== slug)
  return [slug, ...filtered].slice(0, cap)
}
